import { Label as FluentLabel } from "@fluentui/react/lib/Label"
import { Stack } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet, mergeStyles } from "@fluentui/react/lib/Styling"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React from "react"

import { ParameterTO } from "@encoway/c-services-js-client"
import { ComponentFactory } from "@encoway/react-configurator"

import { ComponentName } from "../../constants"
import { ParameterStyles } from "./Parameter.styles"
import { ParameterProps, IParameterStyles } from "./Parameter.types"

interface LabelProps extends ParameterProps {
    classNames: IProcessedStyleSet<IParameterStyles>
}

const Label = (props: LabelProps) => {
    if (props.hideLabel || !props.data) {
        return null
    } else {
        const defaultRenderLabel = (data?: ParameterTO) => {
            return (
                <FluentLabel className={props.classNames.label}>
                    {data?.translatedName}
                </FluentLabel>
            )
        }
        return props.onRenderLabel
            ? props.onRenderLabel(props.data, defaultRenderLabel)
            : defaultRenderLabel(props.data)
    }
}
const InfoButton = (props: ParameterProps) => {
    if (props.hideInfoButton) {
        return null
    } else {
        return ComponentFactory.instanceOf(ComponentName.InfoButton, props)
    }
}
const Quantity = (props: ParameterProps) => {
    if (props.hideQuantity) {
        return null
    } else {
        return ComponentFactory.instanceOf(ComponentName.Quantity, props)
    }
}
const StateIcon = (props: ParameterProps) => {
    let stateiconProps = props as any
    if (props.hideStateIcon) {
        if (props.hideQuantity) {
            return null
        }
        stateiconProps = {
            ...stateiconProps,
            onlyPlaceholder: true
        }
    }
    return ComponentFactory.instanceOf(ComponentName.StateIcon, stateiconProps)
}

const AdditionalValue = (props: ParameterProps) => {
    if (!props.data.additionalValue) {
        return null
    }
    return ComponentFactory.instanceOf(ComponentName.AdditionalValue, props)
}

const BurgerMenu = (props: ParameterProps) => {
    if (!props.data.additionalValue && props.hideStateIcon) {
        if (props.hideQuantity) {
            return null
        }
    }
    const menueProps = {
        ...(props as any),
        onlyPlaceholder: props.hideBurgermenu
    }
    return ComponentFactory.instanceOf(ComponentName.BurgerMenu, menueProps)
}

/**
 * The Parameter component displays meta information for parameters and positions.
 * This includes the title, as well as the InfoButton, Quantity and StateIcon components.
 *
 * Widgets, such as the Checkbox component contain a Parameter component. The reason for this is that widgets use the Parameter component in different ways.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Parameter/Parameter.tsx)
 * - [ParameterStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Parameter/Parameter.styles.ts)
 * - [ParameterProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/configurator-components/src/components/Parameter/Parameter.types.ts)
 *
 * @visibleName Parameter
 */
const IParameter = (props: ParameterProps) => {
    const { styles, theme, ...delegatedProps } = props
    const classNames = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IParameterStyles>
    const visible =
        !props.hideLabel ||
        !props.hideInfoButton ||
        !props.hideQuantity ||
        !props.hideStateIcon ||
        !props.hideBurgermenu
    const className = visible
        ? mergeStyles(classNames.root, classNames.visible)
        : classNames.root

    return (
        <div>
            <AdditionalValue {...delegatedProps} />
            <Stack horizontal className={className}>
                <Label {...delegatedProps} classNames={classNames} />
                <InfoButton {...delegatedProps} />
                <Stack horizontal grow={2} />
                <Quantity {...delegatedProps} />
                <StateIcon {...delegatedProps} />
                <BurgerMenu {...delegatedProps} />
            </Stack>
        </div>
    )
}

/**
 * The Parameter component displays meta information for parameters and positions.
 * This includes the title, as well as the InfoButton, Quantity and StateIcon components.
 *
 * @see ParameterStyles
 * @see ParameterProps
 */
export const Parameter = styled(IParameter, ParameterStyles)
Parameter.displayName = "Parameter"
